import React, { useState, useRef, useCallback, useMemo, useEffect, useLayoutEffect } from "react";
import clsx from "clsx";
import { useShallow } from "zustand/react/shallow";

import { AllCommunityModule, type RowClickedEvent } from "ag-grid-community";
import { AgGridProvider } from "ag-grid-react";
const modules = [AllCommunityModule];

import { Group, Panel, Separator, usePanelRef } from "react-resizable-panels";

import kutil from "@modules/kutil";
import valueFormatter from "@modules/value_formatter";
import { useStableHandlers } from "@modules/stable_handlers";

import PopupCommon from "@components/popup/popup_common";
import { CustomAgGridReact, type CustomColDef, type CustomColGroupDef } from "@components/aggrid/custom_ag_grid_react";

import { usePlcStore } from "@user/store/plc_store";

import WorkOrder from "@user/views/popup/work_order";
import WorkManagement from "@user/views/popup/work_management";
import EquipConfig from "@user/views/popup/equip_config";
import CvBlock from "@user/views/popup/cv_block";
import Legend from "@user/views/popup/legend";

import logo from "@user/assets/images/common/logo-system.svg";

function Main() {
  const [showWorkOrder, setShowWorkOrder] = useState(false); // 팝업(작업지시)
  const [showWorkManagement, setShowWorkManagement] = useState(false); // 팝업(작업관리)
  const [showEquipConfig, setShowEquipConfig] = useState(false); // 팝업(설비설정)
  const [showCvBlock, setShowCvBlock] = useState(false); // 팝업(차단해제)
  const [showLegend, setShowLegend] = useState(false); // 팝업(범례)
  const [workEquip, setWorkEquip] = useState("");

  const mfcIsStarted = usePlcStore(state => state.mfcStatus?.isStarted);

  const sc1IsComm = usePlcStore(state => state.scStatuses.get(1)?.isComm);
  const sc2IsComm = usePlcStore(state => state.scStatuses.get(2)?.isComm);
  const sc3IsComm = usePlcStore(state => state.scStatuses.get(3)?.isComm);
  const sc4IsComm = usePlcStore(state => state.scStatuses.get(4)?.isComm);
  const sc5IsComm = usePlcStore(state => state.scStatuses.get(5)?.isComm);
  const sc6IsComm = usePlcStore(state => state.scStatuses.get(6)?.isComm);
  const sc7IsComm = usePlcStore(state => state.scStatuses.get(7)?.isComm);
  const rv1IsComm = usePlcStore(state => state.rvStatuses.get(1)?.isComm);
  const cv1IsComm = usePlcStore(state => state.cvStatuses.get(101)?.isComm);
  const cv2IsComm = usePlcStore(state => state.cvStatuses.get(201)?.isComm);

  const cv208IsMoving = usePlcStore(state => state.cvStatuses.get(208)?.isMovingAvailable);
  const cv214IsMoving = usePlcStore(state => state.cvStatuses.get(214)?.isMovingAvailable);
  const cv207IsMoving = usePlcStore(state => state.cvStatuses.get(207)?.isMovingAvailable);
  const cv201IsMoving = usePlcStore(state => state.cvStatuses.get(201)?.isMovingAvailable);
  const cv101IsMoving = usePlcStore(state => state.cvStatuses.get(101)?.isMovingAvailable);
  const cv122IsMoving = usePlcStore(state => state.cvStatuses.get(122)?.isMovingAvailable);
  const cv123IsMoving = usePlcStore(state => state.cvStatuses.get(123)?.isMovingAvailable);
  const cv139IsMoving = usePlcStore(state => state.cvStatuses.get(139)?.isMovingAvailable);
  const cv140IsMoving = usePlcStore(state => state.cvStatuses.get(140)?.isMovingAvailable);
  const cv160IsMoving = usePlcStore(state => state.cvStatuses.get(160)?.isMovingAvailable);
  const cv161IsMoving = usePlcStore(state => state.cvStatuses.get(161)?.isMovingAvailable);
  const cv176IsMoving = usePlcStore(state => state.cvStatuses.get(176)?.isMovingAvailable);
  const cv177IsMoving = usePlcStore(state => state.cvStatuses.get(177)?.isMovingAvailable);
  const cv197IsMoving = usePlcStore(state => state.cvStatuses.get(197)?.isMovingAvailable);

  const cv222IsMoving = usePlcStore(state => state.cvStatuses.get(222)?.isMovingAvailable);
  const cv228IsMoving = usePlcStore(state => state.cvStatuses.get(228)?.isMovingAvailable);
  const cv221IsMoving = usePlcStore(state => state.cvStatuses.get(221)?.isMovingAvailable);
  const cv215IsMoving = usePlcStore(state => state.cvStatuses.get(215)?.isMovingAvailable);
  const cv301IsMoving = usePlcStore(state => state.cvStatuses.get(301)?.isMovingAvailable);
  const cv309IsMoving = usePlcStore(state => state.cvStatuses.get(309)?.isMovingAvailable);
  const cv310IsMoving = usePlcStore(state => state.cvStatuses.get(310)?.isMovingAvailable);
  const cv317IsMoving = usePlcStore(state => state.cvStatuses.get(317)?.isMovingAvailable);
  const cv318IsMoving = usePlcStore(state => state.cvStatuses.get(318)?.isMovingAvailable);
  const cv325IsMoving = usePlcStore(state => state.cvStatuses.get(325)?.isMovingAvailable);
  const cv326IsMoving = usePlcStore(state => state.cvStatuses.get(326)?.isMovingAvailable);
  const cv333IsMoving = usePlcStore(state => state.cvStatuses.get(333)?.isMovingAvailable);
  const cv334IsMoving = usePlcStore(state => state.cvStatuses.get(334)?.isMovingAvailable);
  const cv341IsMoving = usePlcStore(state => state.cvStatuses.get(341)?.isMovingAvailable);

  const workDetails = usePlcStore(
    useShallow(state => Array.from(state.workDetails.values())),
  );

  ////////////////////////////////////////////////////////////////////////////////
  // grid 관련 기능들
  ////////////////////////////////////////////////////////////////////////////////
  const gridInfo = useMemo((): (CustomColDef | CustomColGroupDef)[] => [
    { headerName: "작업번호", field: "workNoMfc", width: 100, cellStyle: {textAlign: "center", cursor: "pointer"}, valueFormatter: (p: any) => {
      return p.data.workNoMfc + "(" + p.data.workNoPlc + ")";
    }},
    { headerName: "작업종류", field: "workType", width: 80, cellStyle: {textAlign: "center", cursor: "pointer"}, valueFormatter: valueFormatter.commonCode("workType")},
    { headerName: "출발위치", field: "startPos", width: 120, cellStyle: {textAlign: "center", cursor: "pointer"}, valueFormatter: valueFormatter.location },
    { headerName: "목적위치", field: "endPos", width: 120, cellStyle: {textAlign: "center", cursor: "pointer"}, valueFormatter: valueFormatter.location },
    { headerName: "작업상태", field: "workStatusEquip", width: 80, cellStyle: {textAlign: "center", cursor: "pointer"}, valueFormatter: valueFormatter.commonCode("workStatus") },
    { headerName: "현재위치", field: "currPosEquip", width: 150, cellStyle: {textAlign: "center", cursor: "pointer"}, valueFormatter: (p: any) => {
      return p.data.startPosEquip.padStart(3, "0") + " > " + p.data.currPosEquip.padStart(3, "0") + " > " + p.data.endPosEquip.padStart(3, "0");
    }},
    { headerName: "작업설비", field: "workEquip", width: 80, cellStyle: {textAlign: "center", cursor: "pointer"}, valueFormatter: valueFormatter.commonCode("workEquip") },
    { headerName: "에러번호", field: "errorNo", width: 80, cellStyle: {textAlign: "center", cursor: "pointer"} },
    { headerName: "대기원인", field: "delayCauseDesc", width: 200, cellStyle: {textAlign: "center", cursor: "pointer"} },
    { headerName: "작업지시시각", field: "workReqDate", width: 100, cellStyle: {textAlign: "center", cursor: "pointer"}, valueFormatter: valueFormatter.date("HH:mm:ss") },
    { headerName: "참고사항", field: "memo", width: 250, cellStyle: {textAlign: "left", cursor: "pointer"} },
  ], []);

  const gridOptions = useMemo(() => ({
    getRowId: (p: any) => "" + p.data.workNo,
  }), []);

  const gridCallbacks = useStableHandlers({
    onRowClicked: (e: RowClickedEvent) => {
      selectedWork.current = e.data;
      setShowWorkManagement(true)
    },
  });

  const selectedWork = useRef(null);

  const workPanelRef = usePanelRef();

  function openEquipConfig(workEquip: string) {
    setWorkEquip(workEquip);
    setShowEquipConfig(true);
  }

  return (
    <>
      <div className="monitoring-wrap">
        {/* 통신 */}
        <div className="communication-status">
          <img alt="MFC" src={logo} />
          {/* 통신 상태 */}
          <div>
            <div>
              <p>MFC</p>
              <span className={clsx({"on": mfcIsStarted})} onDoubleClick={() => openEquipConfig("PLCManager")}></span>
            </div>
            <div>
              <p>STC1</p>
              <span className={clsx({"on": sc1IsComm})} onDoubleClick={() => openEquipConfig("SC01")} ></span>
            </div>
            <div>
              <p>STC2</p>
              <span className={clsx({"on": sc2IsComm})} onDoubleClick={() => openEquipConfig("SC02")}></span>
            </div>
            <div>
              <p>STC3</p>
              <span className={clsx({"on": sc3IsComm})} onDoubleClick={() => openEquipConfig("SC03")}></span>
            </div>
            <div>
              <p>STC4</p>
              <span className={clsx({"on": sc4IsComm})} onDoubleClick={() => openEquipConfig("SC04")}></span>
            </div>
            <div>
              <p>STC5</p>
              <span className={clsx({"on": sc5IsComm})} onDoubleClick={() => openEquipConfig("SC05")}></span>
            </div>
            <div>
              <p>STC6</p>
              <span className={clsx({"on": sc6IsComm})} onDoubleClick={() => openEquipConfig("SC06")}></span>
            </div>
            <div>
              <p>STC7</p>
              <span className={clsx({"on": sc7IsComm})} onDoubleClick={() => openEquipConfig("SC07")}></span>
            </div>
            <div>
              <p>RGV1</p>
              <span className={clsx({"on": rv1IsComm})} onDoubleClick={() => openEquipConfig("RV41")}></span>
            </div>
            <div>
              <p>PLC1</p>
              <span className={clsx({"on": cv1IsComm})} onDoubleClick={() => openEquipConfig("CV31")}></span>
            </div>
            <div>
              <p>PLC2</p>
              <span className={clsx({"on": cv2IsComm})} onDoubleClick={() => openEquipConfig("CV32")}></span>
            </div>
          </div>
        </div>
        {/*// 통신 */}
        <Group orientation="vertical" className="panel-group">
          <Panel id="map" minSize="30%">
            {/* 모니터링 */}
            <div className="monitoring">
              <div>
                {/* monitoring-contents */}
                <div className="monitoring-con">
                  <div>
                    {/* 1층 */}
                    <div className="warehouse-1F">
                      <p>1F</p>
                      {/* pallet */}
                      <div className="pallet">
                        {/* line01 */}
                        <div className="empty-area"></div>
                        <CV type="ALL" id={210} />
                        <CV type="HOR" id={209} prefix="◀" style={{ gridColumn: "3 / span 3" }} />
                        <CV type="HOR" id={208} />
                        <div className="info-area" style={{ gridColumn: "7 / span 2" }}>
                          <span className={clsx({"status-lamp":true, "lamp-normal": cv208IsMoving, "lamp-error": !cv208IsMoving})}></span>
                        </div>
                        {/* line02 */}
                        <div className="info-area">
                          <span className="badge">2-2</span>
                        </div>
                        <CV type="VER" id={211} postfix="<br />▼" />
                        <div className="stc-number">
                          <p>7</p>
                        </div>
                        {/* 스태커 7 */}
                        <STC id={7} floor={1} className="stc-area stc-f1 stc-pallet" style={{ gridColumn: "4 / span 5" }} />
                        {/* line03 */}
                        <div className="info-area">
                          <p>1-5</p>
                          <span className="status-lamp lamp-disabled"></span>
                        </div>
                        <CV type="ALL" id={212} />
                        <CV type="HOR" id={213} postfix="▶" style={{ gridColumn: "3 / span 3" }} />
                        <CV type="HOR" id={214} />
                        <div className="info-area" style={{ gridColumn: "7 / span 2" }}>
                          <span className={clsx({"status-lamp":true, "lamp-normal": cv214IsMoving, "lamp-error": !cv214IsMoving})}></span>
                        </div>
                        {/* line04 */}
                        <div className="empty-area" style={{ gridColumn: "2 / span 7" }}></div>
                        {/* line05 */}
                        <div className="info-area">
                          <p>1-4</p>
                          <span className="status-lamp lamp-disabled"></span>
                        </div>
                        <CV type="ALL" id={205} />
                        <CV type="HOR" id={206} postfix="▶" style={{ gridColumn: "3 / span 3" }} />
                        <CV type="HOR" id={207} />
                        <div className="info-area" style={{ gridColumn: "7 / span 2" }}>
                          <span className={clsx({"status-lamp":true, "lamp-normal": cv207IsMoving, "lamp-error": !cv207IsMoving})}></span>
                        </div>
                        {/* line06 */}
                        <div className="info-area">
                          <span className="badge">2-1</span>
                        </div>
                        <CV type="VER" id={204} prefix="▲<br />" />
                        <div className="stc-number">
                          <p>6</p>
                        </div>
                        {/* 스태커 6 */}
                        <STC id={6} floor={1} className="stc-area stc-f1 stc-pallet" style={{ gridColumn: "4 / span 5" }} />
                        {/* line07 */}
                        <div className="empty-area"></div>
                        <CV type="ALL" id={203} />
                        <CV type="HOR" id={202} prefix="◀" style={{ gridColumn: "3 / span 3" }} />
                        <CV type="HOR" id={201} />
                        <div className="info-area" style={{ gridColumn: "7 / span 2" }}>
                          <span className={clsx({"status-lamp":true, "lamp-normal": cv201IsMoving, "lamp-error": !cv201IsMoving})}></span>
                        </div>
                      </div>
                      {/*// pallet */}
                      {/* bucket */}
                      <div className="bucket">
                        {/* line01 */}
                        <div className="empty-area"></div>
                        <CV type="ALL" id={111} />
                        <CV type="HOR" id={110} prefix="◀" />
                        <CV type="ALL" id={108} />
                        <CV type="HOR" id={107} prefix="◀" />
                        <CV type="ALL" id={106} />
                        <CV type="HOR" id={104} prefix="◀" />
                        <CV type="HOR" id={103} className="bd-left" prefix="◀" />
                        <CV type="HOR" id={102} className="bd-left" prefix="◀" />
                        <CV type="HOR" id={101} />
                        <div className="info-area" style={{ gridColumn: "11 / span 2" }}>
                          <span className={clsx({"status-lamp":true, "lamp-normal": cv101IsMoving, "lamp-error": !cv101IsMoving})}></span>
                        </div>
                        {/* line02 */}
                        <div className="info-area">
                          <p>1-3</p>
                          <span className="status-lamp lamp-disabled"></span>
                          <span className="badge">1-1</span>
                        </div>
                        <CV type="VER" id={112} postfix="<br />▼" />
                        <div className="empty-area"></div>
                        <CV type="VER" id={109} postfix="<br />▼" />
                        <div className="empty-area"></div>
                        <CV type="VER" id={105} prefix="▲<br />" />
                        <div className="stc-number">
                          <p>5</p>
                        </div>
                        {/* 스태커 5 */}
                        <STC id={5} floor={1} className="stc-area stc-f1 stc-bucket" style={{ gridColumn: "8 / span 5" }} />
                        {/* line03 */}
                        <div className="empty-area"></div>
                        <CV type="ALL" id={113} />
                        <CV type="HOR" id={114} postfix="▶" />
                        <CV type="ALL" id={115} />
                        <CV type="HOR" id={116} postfix="▶" />
                        <CV type="ALL" id={118} />
                        <CV type="HOR" id={119} postfix="▶" />
                        <CV type="HOR" id={120} className="bd-left" postfix="▶" />
                        <CV type="HOR" id={121} className="bd-left" postfix="▶" />
                        <CV type="HOR" id={122} />
                        <div className="info-area" style={{ gridColumn: "11 / span 2" }}>
                          <span className={clsx({"status-lamp":true, "lamp-normal": cv122IsMoving, "lamp-error": !cv122IsMoving})}></span>
                        </div>
                        {/* line04 */}
                        <div className="empty-area" style={{ gridColumn: "1 / span 3" }}></div>
                        <CV type="VER" id={117} postfix="<br />▼" />
                        <div className="empty-area"></div>
                        <CV type="VER" id={129} prefix="▲<br />" />
                        <div className="empty-area" style={{ gridColumn: "7 / span 6" }}></div>
                        {/* line05 */}
                        <div className="empty-area" style={{ gridColumn: "1 / span 2" }}></div>
                        <div className="info-area">
                          <span className="badge">1-2</span>
                        </div>
                        <CV type="ALL" id={131} />
                        <CV type="HOR" id={130} prefix="◀" />
                        <CV type="ALL" id={128} />
                        <CV type="HOR" id={126} prefix="◀" />
                        <CV type="HOR" id={125} prefix="◀" className="bd-left" />
                        <CV type="HOR" id={124} prefix="◀" className="bd-left" />
                        <CV type="HOR" id={123} />
                        <div className="info-area" style={{ gridColumn: "11 / span 2" }}>
                          <span className={clsx({"status-lamp":true, "lamp-normal": cv123IsMoving, "lamp-error": !cv123IsMoving})}></span>
                        </div>
                        {/* line06 */}
                        <div className="empty-area" style={{ gridColumn: "1 / span 3" }}></div>
                        <CV type="VER" id={132} postfix="<br />▼" />
                        <div className="empty-area"></div>
                        <CV type="VER" id={127} prefix="▲<br />" />
                        <div className="stc-number">
                          <p>4</p>
                        </div>
                        {/* 스태커 4 */}
                        <STC id={4} floor={1} className="stc-area stc-f1 stc-bucket" style={{ gridColumn: "8 / span 5" }} />
                        {/* line07 */}
                        <div className="empty-area" style={{ gridColumn: "1 / span 3" }}></div>
                        <CV type="ALL" id={133} />
                        <CV type="HOR" id={134} postfix="▶" />
                        <CV type="ALL" id={135} />
                        <CV type="HOR" id={136} postfix="▶" />
                        <CV type="HOR" id={137} postfix="▶" className="bd-left" />
                        <CV type="HOR" id={138} postfix="▶" className="bd-left xxx" />
                        <CV type="HOR" id={139} />
                        <div className="info-area" style={{ gridColumn: "11 / span 2" }}>
                          <span className={clsx({"status-lamp":true, "lamp-normal": cv139IsMoving, "lamp-error": !cv139IsMoving})}></span>
                        </div>
                        {/* line08*/}
                        <div className="empty-area" style={{ gridColumn: "1 / span 12" }}></div>
                        {/* line09 */}
                        <div className="empty-area"></div>
                        <CV type="ALL" id={150} />
                        <CV type="HOR" id={149} prefix="◀" />
                        <CV type="ALL" id={147} />
                        <CV type="HOR" id={146} prefix="◀" />
                        <CV type="ALL" id={145} />
                        <CV type="HOR" id={143} prefix="◀" />
                        <CV type="HOR" id={142} prefix="◀" className="bd-left" />
                        <CV type="HOR" id={141} prefix="◀" className="bd-left" />
                        <CV type="HOR" id={140} />
                        <div className="info-area" style={{ gridColumn: "11 / span 2" }}>
                          <span className={clsx({"status-lamp":true, "lamp-normal": cv140IsMoving, "lamp-error": !cv140IsMoving})}></span>
                        </div>
                        {/* line10 */}
                        <div className="info-area">
                          <p>1-2</p>
                          <span className="status-lamp lamp-disabled"></span>
                          <span className="badge">1-3</span>
                        </div>
                        <CV type="VER" id={151} postfix="<br />▼" />
                        <div className="empty-area"></div>
                        <CV type="VER" id={148} postfix="<br />▼" />
                        <div className="empty-area"></div>
                        <CV type="VER" id={144} prefix="▲<br />" />
                        <div className="stc-number">
                          <p>3</p>
                        </div>
                        {/* 스태커 3 */}
                        <STC id={3} floor={1} className="stc-area stc-f1 stc-bucket" style={{ gridColumn: "8 / span 5" }} />
                        {/* line11 */}
                        <div className="empty-area"></div>
                        <CV type="ALL" id={152} />
                        <CV type="HOR" id={153} postfix="▶" />
                        <CV type="ALL" id={154} />
                        <CV type="HOR" id={155} postfix="▶" />
                        <CV type="ALL" id={156} />
                        <CV type="HOR" id={157} postfix="▶" />
                        <CV type="HOR" id={158} postfix="▶" className="bd-left" />
                        <CV type="HOR" id={159} postfix="▶" className="bd-left" />
                        <CV type="HOR" id={160} />
                        <div className="info-area" style={{ gridColumn: "11 / span 2" }}>
                          <span className={clsx({"status-lamp":true, "lamp-normal": cv160IsMoving, "lamp-error": !cv160IsMoving})}></span>
                        </div>
                        {/* line12*/}
                        <div className="empty-area" style={{ gridColumn: "1 / span 12" }}></div>
                        {/* line13 */}
                        <div className="empty-area" style={{ gridColumn: "1 / span 3" }}></div>
                        <CV type="ALL" id={168} />
                        <CV type="HOR" id={167} prefix="◀" />
                        <CV type="ALL" id={165} />
                        <CV type="HOR" id={164} prefix="◀" />
                        <CV type="HOR" id={163} prefix="◀" className="bd-left" />
                        <CV type="HOR" id={162} prefix="◀" className="bd-left" />
                        <CV type="HOR" id={161} />
                        <div className="info-area" style={{ gridColumn: "11 / span 2" }}>
                          <span className={clsx({"status-lamp":true, "lamp-normal": cv161IsMoving, "lamp-error": !cv161IsMoving})}></span>
                        </div>
                        {/* line14 */}
                        <div className="empty-area" style={{ gridColumn: "1 / span 2" }}></div>
                        <div className="info-area">
                          <span className="badge">1-4</span>
                        </div>
                        <CV type="VER" id={169} postfix="<br />▼" />
                        <div className="empty-area"></div>
                        <CV type="VER" id={166} prefix="▲<br />" />
                        <div className="stc-number">
                          <p>2</p>
                        </div>
                        {/* 스태커 2 */}
                        <STC id={2} floor={1} className="stc-area stc-f1 stc-bucket" style={{ gridColumn: "8 / span 5" }} />
                        {/* line15 */}
                        <div className="empty-area" style={{ gridColumn: "1 / span 3" }}></div>
                        <CV type="ALL" id={170} />
                        <CV type="HOR" id={171} postfix="▶" />
                        <CV type="ALL" id={172} />
                        <CV type="HOR" id={173} postfix="▶" />
                        <CV type="HOR" id={174} postfix="▶" className="bd-left" />
                        <CV type="HOR" id={175} postfix="▶" className="bd-left" />
                        <CV type="HOR" id={176} />
                        <div className="info-area" style={{ gridColumn: "11 / span 2" }}>
                          <span className={clsx({"status-lamp":true, "lamp-normal": cv176IsMoving, "lamp-error": !cv176IsMoving})}></span>
                        </div>
                        {/* line16*/}
                        <div className="empty-area" style={{ gridColumn: "1 / span 12" }}></div>
                        {/* line17 */}
                        <div className="empty-area"></div>
                        <CV type="ALL" id={187} />
                        <CV type="HOR" id={186} prefix="◀" />
                        <CV type="ALL" id={184} />
                        <CV type="HOR" id={183} prefix="◀" />
                        <CV type="ALL" id={181} />
                        <CV type="HOR" id={180} prefix="◀" />
                        <CV type="HOR" id={179} prefix="◀" className="bd-left" />
                        <CV type="HOR" id={178} prefix="◀" className="bd-left" />
                        <CV type="HOR" id={177} />
                        <div className="info-area" style={{ gridColumn: "11 / span 2" }}>
                          <span className={clsx({"status-lamp":true, "lamp-normal": cv177IsMoving, "lamp-error": !cv177IsMoving})}></span>
                        </div>
                        {/* line18 */}
                        <div className="info-area">
                          <p>1-1</p>
                          <span className="status-lamp lamp-disabled"></span>
                          <span className="badge">1-5</span>
                        </div>
                        <CV type="VER" id={188} postfix="<br />▼" />
                        <div className="empty-area"></div>
                        <CV type="VER" id={185} postfix="<br />▼" />
                        <div className="empty-area"></div>
                        <CV type="VER" id={182} prefix="▲<br />" />
                        <div className="stc-number">
                          <p>1</p>
                        </div>
                        {/* 스태커 1 */}
                        <STC id={1} floor={1} className="stc-area stc-f1 stc-bucket" style={{ gridColumn: "8 / span 5" }} />
                        {/* line19 */}
                        <div className="empty-area"></div>
                        <CV type="ALL" id={189} />
                        <CV type="HOR" id={190} postfix="▶" />
                        <CV type="ALL" id={191} />
                        <CV type="HOR" id={192} postfix="▶" />
                        <CV type="ALL" id={193} />
                        <CV type="HOR" id={194} postfix="▶" />
                        <CV type="HOR" id={195} postfix="▶" className="bd-left" />
                        <CV type="HOR" id={196} postfix="▶" className="bd-left" />
                        <CV type="HOR" id={197} />
                        <div className="info-area" style={{ gridColumn: "11 / span 2" }}>
                          <span className={clsx({"status-lamp":true, "lamp-normal": cv197IsMoving, "lamp-error": !cv197IsMoving})}></span>
                        </div>
                      </div>
                      {/*// bucket */}
                    </div>
                    {/*// 1층 */}
                    {/* 2층 */}
                    <div className="warehouse-2F">
                      <p>2F</p>
                      {/* pallet */}
                      <div className="pallet">
                        {/* line01 */}
                        <div className="empty-area"></div>
                        <CV type="ALL" id={224} />
                        <CV type="HOR" id={223} prefix="◀" style={{ gridColumn: "3 / span 2" }} />
                        <CV type="HOR" id={222} />
                        <div className="pallet-area">
                          <div>
                            {kutil.range(1, 18).map((idx) => (
                              <p key={idx}></p>
                            ))}
                          </div>
                        </div>
                        <div className="empty-area"></div>
                        {/* line02 */}
                        <div className="info-area">
                          <span className="badge">2-4</span>
                        </div>
                        <CV type="VER" id={225} postfix="<br />▼" />
                        <div className="empty-area" style={{ gridColumn: "3 / span 2" }}></div>
                        {/* 스태커 7 */}
                        <STC id={7} floor={2} className="stc-area stc-f2 stc-pallet" style={{ gridColumn: "5 / span 3" }} />
                        {/* line03 */}
                        <div className="info-area">
                          <p>2-3</p>
                          <span className="status-lamp lamp-disabled"></span>
                        </div>
                        <CV type="ALL" id={226} />
                        <CV type="HOR" id={227} postfix="▶" style={{ gridColumn: "3 / span 2" }} />
                        <CV type="HOR" id={228} />
                        <div className="pallet-area">
                          <div>
                            {kutil.range(1, 18).map((idx) => (
                              <p key={idx}></p>
                            ))}
                          </div>
                        </div>
                        <div className="empty-area"></div>
                        {/* line04 */}
                        <div className="empty-area" style={{ gridColumn: "1 / span 7" }}></div>
                        {/* line05 */}
                        <div className="info-area">
                          <p>2-2</p>
                          <span className="status-lamp lamp-disabled"></span>
                        </div>
                        <CV type="ALL" id={219} />
                        <CV type="HOR" id={220} postfix="▶" style={{ gridColumn: "3 / span 2" }} />
                        <CV type="HOR" id={221} />
                        <div className="pallet-area">
                          <div>
                            {kutil.range(1, 18).map((idx) => (
                              <p key={idx}></p>
                            ))}
                          </div>
                        </div>
                        <div className="empty-area"></div>
                        {/* line06 */}
                        <div className="info-area">
                          <span className="badge">2-3</span>
                        </div>
                        <CV type="VER" id={218} prefix="▲<br />" />
                        <div className="empty-area" style={{ gridColumn: "3 / span 2" }}></div>
                        {/* 스태커 6 */}
                        <STC id={6} floor={2} className="stc-area stc-f2 stc-pallet" style={{ gridColumn: "5 / span 3" }} />
                        {/* line07 */}
                        <div className="empty-area"></div>
                        <CV type="ALL" id={217} />
                        <CV type="HOR" id={216} prefix="◀" style={{ gridColumn: "3 / span 2" }} />
                        <CV type="HOR" id={215} />
                        <div className="pallet-area">
                          <div>
                            {kutil.range(1, 18).map((idx) => (
                              <p key={idx}></p>
                            ))}
                          </div>
                        </div>
                        <div className="empty-area"></div>
                      </div>
                      {/*// pallet */}
                      {/* bucket */}
                      <div className="bucket">
                        {/* line01 */}
                        <div className="empty-area" style={{ gridColumn: "1 / span 3" }}></div>
                        <CV type="ALL" id={304} />
                        <CV type="HOR" id={303} prefix="◀" />
                        <CV type="HOR" id={302} prefix="◀" className="bd-left" />
                        <CV type="HOR" id={301} />
                        <div className="bucket-area">
                          <div>
                            {kutil.range(1, 45).map((idx) => (
                              <p key={idx}></p>
                            ))}
                          </div>
                        </div>
                        <div className="empty-area"></div>
                        {/* line02 */}
                        <div className="empty-area"></div>
                        <div className="info-area">
                          <span className="badge">1-6</span>
                        </div>
                        {/* RGV 1 */}
                        <RGV id={1} className="rgv-area" style={{ gridColumn: "3", gridRow: "2 / span 19" }} />
                        <CV type="VER" id={305} postfix="<br />▼" />
                        <div className="empty-area" style={{ gridColumn: "5 / span 2" }}></div>
                        {/* 스태커 5 */}
                        <STC id={5} floor={2} className="stc-area stc-f2 stc-bucket" style={{ gridColumn: "7 / span 3" }} />
                        {/* line03 */}
                        <div className="empty-area"></div>
                        <CV type="ALL" id={401} />
                        <CV type="ALL" id={306} />
                        <CV type="HOR" id={307} postfix="▶" />
                        <CV type="HOR" id={308} postfix="▶" className="bd-left" />
                        <CV type="HOR" id={309} />
                        <div className="bucket-area">
                          <div>
                            {kutil.range(1, 45).map((idx) => (
                              <p key={idx}></p>
                            ))}
                          </div>
                        </div>
                        <div className="empty-area"></div>
                        {/* line04 */}
                        <div className="info-area">
                          <span className="badge">1-11</span>
                        </div>
                        <CV type="VER" id={402} postfix="<br />▼" />
                        <div className="info-area reverse" style={{ gridColumn: "4 / span 3" }}></div>
                        <div className="empty-area" style={{ gridColumn: "7 / span 3" }}></div>
                        {/* line05 */}
                        <div className="info-area">
                          <p>2-1</p>
                          <span className="status-lamp lamp-disabled"></span>
                        </div>
                        <CV type="ALL" id={403} />
                        <div className="empty-area" style={{ gridColumn: "4 / span 6" }}></div>
                        {/* line06 */}
                        <div className="empty-area" style={{ gridColumn: "1 / span 2" }}></div>
                        <CV type="ALL" id={313} />
                        <CV type="HOR" id={312} prefix="◀" />
                        <CV type="HOR" id={311} prefix="◀" className="bd-left" />
                        <CV type="HOR" id={310} />
                        <div className="bucket-area">
                          <div>
                            {kutil.range(1, 45).map((idx) => (
                              <p key={idx}></p>
                            ))}
                          </div>
                        </div>
                        <div className="empty-area"></div>
                        {/* line07 */}
                        <div className="empty-area"></div>
                        <div className="info-area">
                          <span className="badge">1-7</span>
                        </div>
                        <div className="empty-area" style={{ gridColumn: "4 / span 3" }}></div>
                        {/* 스태커 4 */}
                        <STC id={4} floor={2} className="stc-area stc-f2 stc-bucket" style={{ gridColumn: "7 / span 3" }} />
                        {/* line08 */}
                        <div className="empty-area" style={{ gridColumn: "1 / span 2" }}></div>
                        <CV type="ALL" id={314} />
                        <CV type="HOR" id={315} postfix="▶" />
                        <CV type="HOR" id={316} postfix="▶" className="bd-left" />
                        <CV type="HOR" id={317} />
                        <div className="bucket-area">
                          <div>
                            {kutil.range(1, 45).map((idx) => (
                              <p key={idx}></p>
                            ))}
                          </div>
                        </div>
                        <div className="empty-area"></div>
                        {/* line09 */}
                        <div className="empty-area" style={{ gridColumn: "1 / span 2" }}></div>
                        <div className="empty-area" style={{ gridColumn: "4 / span 6" }}></div>
                        {/* line10 */}
                        <div className="empty-area" style={{ gridColumn: "1 / span 2" }}></div>
                        <CV type="ALL" id={321} />
                        <CV type="HOR" id={320} prefix="◀" />
                        <CV type="HOR" id={319} prefix="◀" className="bd-left" />
                        <CV type="HOR" id={318} />
                        <div className="bucket-area">
                          <div>
                            {kutil.range(1, 45).map((idx) => (
                              <p key={idx}></p>
                            ))}
                          </div>
                        </div>
                        <div className="empty-area"></div>
                        {/* line11*/}
                        <div className="empty-area"></div>
                        <div className="info-area">
                          <span className="badge">1-8</span>
                        </div>
                        <div className="empty-area" style={{ gridColumn: "4 / span 3" }}></div>
                        {/* 스태커 3 */}
                        <STC id={3} floor={2} className="stc-area stc-f2 stc-bucket" style={{ gridColumn: "7 / span 3" }} />
                        {/* line12 */}
                        <div className="empty-area" style={{ gridColumn: "1 / span 2" }}></div>
                        <CV type="ALL" id={322} />
                        <CV type="HOR" id={323} postfix="▶" />
                        <CV type="HOR" id={324} postfix="▶" className="bd-left" />
                        <CV type="HOR" id={325} />
                        <div className="bucket-area">
                          <div>
                            {kutil.range(1, 45).map((idx) => (
                              <p key={idx}></p>
                            ))}
                          </div>
                        </div>
                        <div className="empty-area"></div>
                        {/* line13 */}
                        <div className="empty-area" style={{ gridColumn: "1 / span 2" }}></div>
                        <div className="empty-area" style={{ gridColumn: "4 / span 6" }}></div>
                        {/* line14 */}
                        <div className="empty-area" style={{ gridColumn: "1 / span 2" }}></div>
                        <CV type="ALL" id={329} />
                        <CV type="HOR" id={328} prefix="◀" />
                        <CV type="HOR" id={327} prefix="◀" className="bd-left" />
                        <CV type="HOR" id={326} />
                        <div className="bucket-area">
                          <div>
                            {kutil.range(1, 45).map((idx) => (
                              <p key={idx}></p>
                            ))}
                          </div>
                        </div>
                        <div className="empty-area"></div>
                        {/* line15 */}
                        <div className="empty-area"></div>
                        <div className="info-area">
                          <span className="badge">1-9</span>
                        </div>
                        <div className="empty-area" style={{ gridColumn: "4 / span 3" }}></div>
                        {/* 스태커 2 */}
                        <STC id={2} floor={2} className="stc-area stc-f2 stc-bucket" style={{ gridColumn: "7 / span 3" }} />
                        {/* line16 */}
                        <div className="empty-area" style={{ gridColumn: "1 / span 2" }}></div>
                        <CV type="ALL" id={330} />
                        <CV type="HOR" id={331} postfix="▶" />
                        <CV type="HOR" id={332} postfix="▶" className="bd-left" />
                        <CV type="HOR" id={333} />
                        <div className="bucket-area">
                          <div>
                            {kutil.range(1, 45).map((idx) => (
                              <p key={idx}></p>
                            ))}
                          </div>
                        </div>
                        <div className="empty-area"></div>
                        {/* line17 */}
                        <div className="empty-area" style={{ gridColumn: "1 / span 2" }}></div>
                        <div className="empty-area" style={{ gridColumn: "4 / span 6" }}></div>
                        {/* line18 */}
                        <div className="empty-area" style={{ gridColumn: "1 / span 2" }}></div>
                        <CV type="ALL" id={337} />
                        <CV type="HOR" id={336} prefix="◀" />
                        <CV type="HOR" id={335} prefix="◀" className="bd-left" />
                        <CV type="HOR" id={334} />
                        <div className="bucket-area">
                          <div>
                            {kutil.range(1, 45).map((idx) => (
                              <p key={idx}></p>
                            ))}
                          </div>
                        </div>
                        <div className="empty-area"></div>
                        {/* line19*/}
                        <div className="empty-area"></div>
                        <div className="info-area">
                          <span className="badge">1-10</span>
                        </div>
                        <div className="empty-area" style={{ gridColumn: "4 / span 3" }}></div>
                        {/* 스태커 1 */}
                        <STC id={1} floor={2} className="stc-area stc-f2 stc-bucket" style={{ gridColumn: "7 / span 3" }} />
                        {/* line20 */}
                        <div className="empty-area" style={{ gridColumn: "1 / span 2" }}></div>
                        <CV type="ALL" id={338} />
                        <CV type="HOR" id={339} postfix="▶" />
                        <CV type="HOR" id={340} postfix="▶" className="bd-left" />
                        <CV type="HOR" id={341} />
                        <div className="bucket-area">
                          <div>
                            {kutil.range(1, 45).map((idx) => (
                              <p key={idx}></p>
                            ))}
                          </div>
                        </div>
                        <div className="empty-area"></div>
                        {/* line04 */}
                        <div className="info-area reverse" style={{ gridColumn: "4 / span 5" }}>
                          <em>RGV JobPhs : 0</em>
                        </div>
                      </div>
                      {/*// bucket */}
                    </div>
                    {/*// 2층 */}
                  </div>
                </div>
                {/*// monitoring-contents */}
              </div>
            </div>
            {/*// 모니터링 */}
          </Panel>
          <Panel
            id="work"
            panelRef={workPanelRef}
            defaultSize={190}
            minSize={80}
            collapsible
            collapsedSize={37}
            groupResizeBehavior="preserve-pixel-size"
          >
            <div className="work-panel">
              {/* 버튼 영역 */}
              <div className="btn-area">
                <div>
                  <button className="basic outline" onClick={() => setShowWorkOrder(true)}>작업지시</button>
                  <button className="basic outline" onClick={() => setShowCvBlock(true)}>CV차단관리</button>
                  <button className="basic outline" onClick={() => setShowLegend(true)}>범례</button>
                </div>
                <div>
                </div>
              </div>
              {/*// 버튼 영역 */}
              {/* 작업 정보 */}
              <AgGridProvider modules={modules}>
                <div className="work-info">
                  <CustomAgGridReact
                    showInfo={false}
                    columnDefs={gridInfo}
                    rowData={workDetails}
                    defaultColDef={kutil.defaultColDef}
                    getRowId={gridOptions.getRowId}
                    className="grid_area ag-theme-alpine"
                    overlayNoRowsTemplate="<span></span>"
                    onRowClicked={gridCallbacks.onRowClicked}
                  />
                </div>
              </AgGridProvider>
              {/*// 작업 정보 */}
            </div>
          </Panel>
        </Group>
      </div>
      { showWorkOrder && // 팝업(작업지시)
        <PopupCommon onClose={() => setShowWorkOrder(false)}>
          <WorkOrder />
        </PopupCommon>
      }
      { showWorkManagement && // 팝업(작업관리)
        <PopupCommon onClose={() => setShowWorkManagement(false)}>
          <WorkManagement work={selectedWork.current!} />
        </PopupCommon>
      }
      { showEquipConfig && // 팝업(설비설정)
        <PopupCommon onClose={() => setShowEquipConfig(false)}>
          <EquipConfig workEquip={workEquip} />
        </PopupCommon>
      }
      { showCvBlock && // 팝업(차단해제)
        <PopupCommon onClose={() => setShowCvBlock(false)}>
          <CvBlock />
        </PopupCommon>
      }
      {showLegend && // 팝업(범례)
        <PopupCommon onClose={() => setShowLegend(false)}>
          <Legend />
        </PopupCommon>
      }
    </>
  );
}

const STC = React.memo(
  ({id, floor, className, style}: any) => {
    const item = usePlcStore(state => state.scStatuses.get(id))!;
    const currFloor = (item.posX == 0 && item.posY <= 2) ? 1 : 2;
    let leftPos = 0;
    if (currFloor == 2 && item.posX > 0) {
      if (id <= 5) {
        leftPos = 30.16 + (item.posX - 1)* 8.9312;
      } else {
        leftPos = 37.65 + (item.posX - 1) * 22.744;
      }
    }
    return (
      <div className={className} style={style}>
        <div>
          <div>
          {floor == currFloor &&
            <div style={{ left: leftPos + "px" }}>
              <p className={clsx("stc-" + item.mode)}><em>{item.forkType == "1" ? "◀" : (item.forkType == "2" ? "▶" : "■")}</em></p>
              <i></i>
              <div className={clsx(item.workType == "I" && "stc-work-in", item.workType == "O" && "stc-work-out")}><em>{item.workNoMfc > 0 ? item.workNoMfc : ""}</em></div>
              <i></i>
              <span className={clsx("stc-com-" + item.status)} ><em>{(item.posX + "").padStart(2, "0")}-{(item.posY + "").padStart(2, "0")}</em></span>
            </div>
          }
          </div>
        </div>
        <p>
          <em></em><i></i><em></em>
        </p>
      </div>
    )
  }
);

const topPoses: any = {
  0: "0%",
  1: "0%",
  2: "10.4%",
  3: "20.9%",
  4: "35.7%",
  5: "42.3%",
  6: "57.2%",
  7: "63.8%",
  8: "78.6%",
  9: "85.2%",
  10: "100%",
};

const RGV = React.memo(
  ({id, className, style}: any) => {
    const item = usePlcStore(state => state.rvStatuses.get(id))!;
    const topPos = topPoses[item.pos ];
    return (
      <div className={className} style={style}>
        <div>
          <i></i>
          <div>
            <div className={clsx("rgv-com-" + item.status)} style={{ top: topPos }}>
              <div><span className={clsx(item.isLoad && "sku")}>{item.workNoMfc > 0 ? item.workNoMfc : ""}</span></div>
              <span className={clsx("rgv-" + item.mode)}>{id}</span>
            </div>
          </div>
        </div>
        <p>
          <em></em><i></i><em></em>
        </p>
      </div>
    )
  }
);

const CV = React.memo(
  ({id, type, className, style, prefix, postfix}: any) => {
    const item = usePlcStore(state => state.cvStatuses.get(id))!;
    if (prefix != item.labelPrefix || postfix != item.labelPostfix) {
      usePlcStore.getState().updateCVStatus({
        id: item.id,
        ver: item.ver + 1,
        labelPrefix: prefix,
        labelPostfix: postfix,
      });
    }

    async function setBlock(id: number, isBlock: boolean) {
      const msg = isBlock ? "차단하시겠습니까?" : "차단해제하시겠습니까?";
      if (await kutil.confirm(msg) == false) {
        return false;
      }

      try {
        const mode = isBlock ? "set" : "clear";
        const data = await kutil.sendPost("/api/work/cv_block.proc?mode=" + mode, {id}, {
        });

        const msg = isBlock ? "차단되었습니다." : "차단해제되었습니다.";
        await kutil.alert(msg);
      } catch(e) {
        console.log(e);
      }
    }

    return (
      <>
        <div className={clsx(
          "conv-area",
          className,
          type == "HOR" && "hor-area",
          type != "HOR" && "ver-area",
          type == "VER" && "type2",
          (item.type == "IN" || item.type == "OUT") && "bd-right bd-left",
        )} style={style}>
          {
            item.blockType &&
            <i className={clsx(
              "block",
              (item.id < 200) && "block1",
              item.blockType == "UP" && "bar-top",
              item.blockType == "DOWN" && "bar-bottom",
              item.blockType == "LEFT" && "bar-left",
              item.blockType == "RIGHT" && "bar-right",
              item.isBlock && "warning",
              !item.isBlock && "normal"
            )}
            onDoubleClick={() => {
              if (
                item.id == 112
                || item.id == 151
                || item.id == 188
                || item.id == 403
                || item.id == 205
                || item.id == 212
                || item.id == 219
                || item.id == 226
              ) {
                setBlock(item.id, !item.isBlock);
              }
            }}></i>
          }
          {
            (item.type == "IN" || item.type == "OUT") &&
            <i className={clsx("bar-left", item.isLifterAvailable && "normal", item.isLifterAvailable == false && "warning")}></i>
          }
          { (type == "ALL") &&
            <>
              <em className={item.status}></em>
              <div>
                <em className={item.status}></em>
                <div className={item.mode}>
                  <span className={clsx(
                    item.isLoad && item.workNoMfc > 0 && "sku-active",
                    item.isLoad && item.workNoMfc == 0 && "sku-nojob",
                    !item.isLoad && item.workNoMfc > 0 && "sku-unload",
                  )}>
                    <p>{item.text}</p>
                  </span>
                </div>
                <em className={item.status}></em>
              </div>
              <em className={item.status}></em>
            </>
          }
          { (type != "ALL") &&
            <>
              <em className={item.status}></em>
              <div className={item.mode}>
                <span className={clsx(
                  item.isLoad && item.workNoMfc > 0 && "sku-active",
                  item.isLoad && item.workNoMfc == 0 && "sku-nojob",
                  !item.isLoad && item.workNoMfc > 0 && "sku-unload",
                )}>
                  <p>{item.text}</p>
                </span>
              </div>
              <em className={item.status}></em>
            </>
          }
          {
            (item.type == "IN" || item.type == "OUT") &&
            <i className={clsx("bar-right", item.isLifterAvailable && "normal", item.isLifterAvailable == false && "warning")}></i>
          }
        </div>
      </>
    );
  },
);

export default Main;
